/**
 * Recovery from dynamic-import failures caused by stale deployments.
 *
 * --- Why this exists ---------------------------------------------------------
 * When a new build is deployed, the old hashed chunk filenames are removed
 * from the server. A browser that still holds the previous `index.html`
 * (in memory, in the bfcache, or — most often — in the Workbox precache of
 * our service worker) keeps requesting the old filenames. Vercel's SPA
 * rewrite returns `index.html` for those missing assets, so dynamic
 * `import()` sees an HTML response and throws one of:
 *
 *   - TypeError: 'text/html' is not a valid JavaScript MIME type.
 *   - Failed to fetch dynamically imported module: <url>
 *   - Loading chunk N failed.
 *   - Loading CSS chunk N failed.
 *
 * --- Recovery strategy ------------------------------------------------------
 * A single `window.location.reload()` is not enough on its own: if the
 * service worker re-serves the same stale shell, the next navigation hits
 * the same error and the user is stuck in a loop. We therefore escalate
 * across three attempts, persisted per-tab via `sessionStorage`:
 *
 *   1. Best-effort cleanup: unregister every service worker, drop every
 *      `CacheStorage` entry, then reload. This forces the next navigation
 *      to talk to the origin and pick up the fresh `index.html`.
 *   2. Cache-busting navigation: append `?_chunkRecovery=<ts>` to the
 *      current URL. Bypasses any in-memory or HTTP cache that might still
 *      hold the old shell, even if the SW unregister in step 1 was racing
 *      with an in-flight request.
 *   3. Give up: rethrow so `ErrorBoundary` surfaces a clear UI with a
 *      manual reload button. At this point the issue is no longer a
 *      stale deploy and we don't want to reload-loop the user.
 *
 * The counter is wiped automatically once the app reaches a stable boot
 * (see `markBootHealthy` below), so a future stale deploy gets the full
 * recovery budget instead of starting at "give up".
 */

import { lazy, type ComponentType, type LazyExoticComponent } from "react";

const RECOVERY_COUNTER_KEY = "curio_chunk_recovery_attempts";
const RECOVERY_QUERY_PARAM = "_chunkRecovery";
const MAX_RECOVERY_ATTEMPTS = 2;
const HEALTHY_BOOT_DELAY_MS = 10_000;
const CLEANUP_TIMEOUT_MS = 2_000;

const CHUNK_ERROR_PATTERNS = [
  /not a valid JavaScript MIME type/i,
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Loading chunk \d+ failed/i,
  /Loading CSS chunk \d+ failed/i,
  /Importing a module script failed/i,
];

let recoveryInFlight = false;

export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;

  const message =
    error instanceof Error
      ? `${error.name}: ${error.message}`
      : typeof error === "string"
        ? error
        : "";

  if (!message) return false;

  return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}

function readAttemptCount(): number {
  try {
    const raw = sessionStorage.getItem(RECOVERY_COUNTER_KEY);
    if (!raw) return 0;
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
  } catch {
    return 0;
  }
}

function writeAttemptCount(value: number): void {
  try {
    sessionStorage.setItem(RECOVERY_COUNTER_KEY, String(value));
  } catch {
    // sessionStorage may be unavailable (private mode, storage disabled).
    // Worst case the counter resets between attempts; the escalation still
    // works on the *current* attempt because we read/write within one tick.
  }
}

function clearAttemptCount(): void {
  try {
    sessionStorage.removeItem(RECOVERY_COUNTER_KEY);
  } catch {
    // ignore
  }
}

/**
 * Best-effort cleanup of everything that could be re-serving a stale shell:
 *   - all registered service workers
 *   - every entry in `CacheStorage`
 *
 * Bounded by `CLEANUP_TIMEOUT_MS` so a slow browser (iOS Safari < 16) cannot
 * trap us in an indefinite cleanup before the reload happens.
 */
async function clearStaleCaches(): Promise<void> {
  const tasks: Promise<unknown>[] = [];

  if (typeof navigator !== "undefined" && navigator.serviceWorker?.getRegistrations) {
    tasks.push(
      navigator.serviceWorker
        .getRegistrations()
        .then((registrations) =>
          Promise.all(
            registrations.map((registration) =>
              registration.unregister().catch((error: unknown) => {
                console.warn("[chunkErrorHandler] SW unregister failed:", error);
              })
            )
          )
        )
        .catch((error: unknown) => {
          console.warn("[chunkErrorHandler] getRegistrations failed:", error);
        })
    );
  }

  if (typeof caches !== "undefined") {
    tasks.push(
      caches
        .keys()
        .then((keys) =>
          Promise.all(
            keys.map((key) =>
              caches.delete(key).catch((error: unknown) => {
                console.warn(`[chunkErrorHandler] caches.delete(${key}) failed:`, error);
              })
            )
          )
        )
        .catch((error: unknown) => {
          console.warn("[chunkErrorHandler] caches.keys failed:", error);
        })
    );
  }

  if (tasks.length === 0) return;

  await Promise.race([
    Promise.all(tasks),
    new Promise<void>((resolve) => setTimeout(resolve, CLEANUP_TIMEOUT_MS)),
  ]);
}

function navigateWithCacheBuster(): void {
  try {
    const url = new URL(window.location.href);
    url.searchParams.set(RECOVERY_QUERY_PARAM, String(Date.now()));
    window.location.replace(url.toString());
  } catch (error) {
    console.warn("[chunkErrorHandler] cache-buster navigation failed:", error);
    window.location.reload();
  }
}

/**
 * If the error looks like a stale-chunk error, escalate through the
 * recovery stages. Returns `true` when we have taken over (caller should
 * stop further handling; a navigation is happening or about to happen).
 *
 * Returns `false` when the error is not a chunk error or when we have
 * exhausted recovery attempts and the caller should rethrow / surface
 * to the error boundary.
 */
export function handleChunkLoadError(error: unknown, context: string): boolean {
  if (!isChunkLoadError(error)) return false;

  if (recoveryInFlight) {
    return true;
  }

  const attempt = readAttemptCount();

  if (attempt >= MAX_RECOVERY_ATTEMPTS) {
    console.warn(
      `[chunkErrorHandler] Stale chunk in "${context}" after ${attempt} recovery attempt(s); giving up.`,
      error
    );
    return false;
  }

  recoveryInFlight = true;
  writeAttemptCount(attempt + 1);

  if (attempt === 0) {
    console.warn(
      `[chunkErrorHandler] Stale chunk in "${context}"; clearing service worker + caches and reloading.`,
      error
    );
    void clearStaleCaches().finally(() => {
      window.location.reload();
    });
    return true;
  }

  console.warn(
    `[chunkErrorHandler] Stale chunk in "${context}" persisted after cleanup; navigating with cache-buster.`,
    error
  );
  navigateWithCacheBuster();
  return true;
}

/**
 * Call once the app has rendered successfully for long enough that we
 * are confident the build is healthy. Resets the per-tab attempt counter
 * so the next stale-chunk situation gets the full recovery budget.
 */
export function markBootHealthy(): void {
  if (typeof window === "undefined") return;

  window.setTimeout(() => {
    if (recoveryInFlight) return;
    if (readAttemptCount() === 0) return;
    clearAttemptCount();
  }, HEALTHY_BOOT_DELAY_MS);
}

/**
 * Drop-in replacement for `React.lazy` that recovers from stale-chunk
 * errors. On the first failure, escalates through `handleChunkLoadError`
 * (which may unregister the SW, drop caches, and reload). If recovery
 * has already been exhausted, the error is rethrown so React's error
 * boundary can surface a real problem.
 */
export function lazyWithRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>
): LazyExoticComponent<T> {
  return lazy(() =>
    factory().catch((error: unknown) => {
      const tookOver = handleChunkLoadError(error, "lazyWithRetry");
      if (tookOver) {
        return new Promise<{ default: T }>(() => {});
      }
      throw error;
    })
  );
}

/**
 * Wrapper for non-React dynamic imports (lazy-loaded libraries like
 * KaTeX, highlight.js, onnxruntime-web). Same escalation as
 * `lazyWithRetry`; returns a never-resolving promise while a recovery
 * navigation is in flight, otherwise rethrows.
 */
export function safeDynamicImport<T>(factory: () => Promise<T>, context: string): Promise<T> {
  return factory().catch((error: unknown) => {
    const tookOver = handleChunkLoadError(error, context);
    if (tookOver) {
      return new Promise<T>(() => {});
    }
    throw error;
  });
}

/**
 * Install global listeners that catch chunk load errors bubbling up from
 * anywhere (Vite's `vite:preloadError`, unhandled promise rejections, and
 * window `error` events). Must be called once at app startup, as early as
 * possible — before any dynamic import has a chance to fail.
 */
export function installChunkErrorHandlers(): void {
  if (typeof window === "undefined") return;

  window.addEventListener("vite:preloadError", (event) => {
    const preloadEvent = event as Event & { payload?: unknown };
    const tookOver = handleChunkLoadError(preloadEvent.payload ?? event, "vite:preloadError");
    if (tookOver) {
      event.preventDefault();
    }
  });

  window.addEventListener("unhandledrejection", (event) => {
    if (handleChunkLoadError(event.reason, "unhandledrejection")) {
      event.preventDefault();
    }
  });

  window.addEventListener("error", (event) => {
    if (handleChunkLoadError(event.error ?? event.message, "window.error")) {
      event.preventDefault();
    }
  });
}
